// Use relative /api/v1 in dev so setupProxy.js forwards /api and /media to the backend (port 3002).
// Set REACT_APP_API_BASE_URL to a full URL (e.g. http://localhost:3002/api/v1) to override.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api/v1";

const DEFAULT_DEV_BACKEND_ORIGIN =
  process.env.REACT_APP_API_ORIGIN || "http://localhost:3002";

/** Origin of the API server (for /media, /uploads, etc.). */
export function getApiOrigin() {
  if (typeof window === "undefined") return "";
  const base = API_BASE_URL;
  if (typeof base === "string" && /^https?:\/\//i.test(base)) {
    try {
      return new URL(base).origin;
    } catch {
      return window.location.origin;
    }
  }
  // Relative /api/v1: same host serves the SPA and (via nginx/proxy) /media and /api.
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_DEV_BACKEND_ORIGIN;
}

/**
 * Absolute URL for paths returned by upload API (e.g. /media/..., /rfq-images/...).
 * Required when REACT_APP_API_BASE_URL points to a different host than the React app.
 */
export function resolvePublicFileUrl(pathOrUrl) {
  if (pathOrUrl == null || pathOrUrl === "") return "";
  let s = String(pathOrUrl).trim();
  // Strip accidental trailing punctuation from pasted/broken URLs (e.g. "...png-")
  if (/\.(png|jpe?g|gif|webp|svg|pdf)(\W+)$/i.test(s)) {
    s = s.replace(/(\.(?:png|jpe?g|gif|webp|svg|pdf))\W+$/i, "$1");
  }
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
  if (s.startsWith("//")) return `${window.location.protocol}${s}`;
  // Normalize Windows-style paths and bare media paths from bulk upload Excel.
  s = s.replace(/\\/g, "/");
  if (!s.startsWith("/") && /^media\//i.test(s)) {
    s = `/${s}`;
  }
  const path = s.startsWith("/") ? s : `/${s}`;
  return `${getApiOrigin()}${path}`;
}

export const API_URLS = {
  LOGIN: "/auth/login",
  USERS: "/users",
  POSTS: "/posts",
  PRODUCTS: "/products",
  // Auction APIs
  AUCTION: {
    BASE: "/auction",
    CREDITS: "/auction/credits",
    MY_CREDITS: "/auction/credits/my",
    CREATE: "/auction",
    MY_AUCTIONS: "/auction/my/auctions",
    ACTIVE: "/auction/active",
    PRICING: "/auction/pricing",
    PUBLISH: (id) => `/auction/${id}/publish`,
    END: (id) => `/auction/${id}/end`,
    CANCEL: (id) => `/auction/${id}/cancel`,
    ACCEPT_BID: (id) => `/auction/${id}/accept-bid`,
    REJECT_BID: (id) => `/auction/${id}/reject-bid`,
    EXTEND: (id) => `/auction/${id}/extend`,
    BIDS: (id) => `/auction/${id}/bids`,
    PLACE_BID: "/auction/bids",
    MY_BIDS: "/auction/my/bids",
    PROFORMA_INVOICE: (id) => `/auction/${id}/proforma-invoice`,
    SEND_PROFORMA_INVOICE_EMAIL: (id) => `/auction/${id}/proforma-invoice/send-email`,
    PAYMENTS: "/auction/payments",
    GET_PAYMENT: (paymentId) => `/auction/payments/${paymentId}`,
    GET_PAYMENT_BY_AUCTION: (auctionId) => `/auction/${auctionId}/payment`,
    COMPLETE_AUCTION_PAYMENT: (auctionId) => `/auction/${auctionId}/payment/complete`,
    UPDATE_PAYMENT: (paymentId) => `/auction/payments/${paymentId}`,
    REOPEN_BID: (auctionId) => `/auction/${auctionId}/reopen-bid`,
    GET_DELIVERY_CONFIRMATION: (auctionId) => `/auction/${auctionId}/delivery-confirmation`,
    UPDATE_BUYER_CONFIRMATION: (confirmationId) => `/auction/delivery-confirmations/${confirmationId}/buyer`,
    UPDATE_SELLER_CONFIRMATION: (confirmationId) => `/auction/delivery-confirmations/${confirmationId}/seller`,
    NOTIFICATIONS: "/auction/notifications/my",
    // Inspection APIs
    CREATE_INSPECTION_RFQ: "/auction/inspection-rfq",
    GET_MY_INSPECTION_RFQS: "/auction/inspection-rfq/my",
    GET_INSPECTION_RFQ: (rfqId) => `/auction/inspection-rfq/${rfqId}`,
    GET_MY_INSPECTION_QUOTATION: (rfqId) => `/auction/inspection-rfq/${rfqId}/my-quotation`,
    GET_INSPECTION_RFQ_BY_AUCTION: (auctionId) => `/auction/${auctionId}/inspection-rfq`,
    GET_QUALITY_INSPECTORS: "/auction/quality-inspectors",
    CREATE_INSPECTION_QUOTATION: "/auction/inspection-quotation",
    GET_INSPECTION_QUOTATIONS: (rfqId) => `/auction/inspection-rfq/${rfqId}/quotations`,
    ACCEPT_INSPECTION_QUOTATION: (quotationId) => `/auction/inspection-quotation/${quotationId}/accept`,
    GET_MY_INSPECTIONS: "/auction/inspection/my",
    START_INSPECTION: (inspectionId) => `/auction/inspection/${inspectionId}/start`,
    GET_INSPECTION: (inspectionId) => `/auction/inspection/${inspectionId}`,
    GET_INSPECTION_BY_AUCTION: (auctionId) => `/auction/${auctionId}/inspection`,
    GET_INSPECTION_DECISION_BY_AUCTION: (auctionId) => `/auction/${auctionId}/inspection-decision`,
    UPDATE_INSPECTION_REPORT: (inspectionId) => `/auction/inspection/${inspectionId}/report`,
    CREATE_INSPECTION_DECISION: "/auction/inspection-decision",
  },
  // QA (buyer QA request flow, inspector APIs)
  QA: {
    QUALITY_INSPECTORS_BY_LOCATION: (params = "") =>
      `/qa/quality-inspectors/location${params ? (params.startsWith("?") ? params : `?${params}`) : ""}`,
    GET_MY_INSPECTIONS: "/qa/inspector/my-inspections",
    /** Inspector dashboard: unified QA requests (buyer–seller + contract manufacturing) */
    INSPECTOR_MY_REQUESTS: "/qa/inspector/my-requests",
    GET_BY_ID: (qaRequestId) => `/qa/${qaRequestId}`,
    QUOTATIONS_BY_REQUEST: (qaRequestId) => `/qa/${qaRequestId}/quotations`,
    QUOTATION_SUBMIT: "/qa/quotation/submit",
    INSPECTION_BY_ID: (inspectionId) => `/qa/inspection/${inspectionId}`,
    /** CM QA: agreed QC payload (inspection checklist items, norms) for inspector external report */
    INSPECTION_QC_REQUIREMENTS: (inspectionId) => `/qa/inspection/${inspectionId}/qc-requirements`,
    INSPECTION_START: (inspectionId) => `/qa/inspection/${inspectionId}/start`,
    INSPECTION_ACCEPT_ASSIGNMENT: (inspectionId) => `/qa/inspection/${inspectionId}/accept-assignment`,
    INSPECTION_MARK_PAID: (inspectionId) => `/qa/inspection/${inspectionId}/mark-paid`,
    INSPECTION_DRAFT: (inspectionId) => `/qa/inspection/${inspectionId}/draft`,
    INSPECTION_COMPLETE: (inspectionId) => `/qa/inspection/${inspectionId}/complete`,
    QUOTATION_BY_ID: (quotationId) => `/qa/quotation/${quotationId}`,
    QUOTATION_BROADCAST: (qaRequestId) => `/qa/quotation/${qaRequestId}/broadcast`,
    BUYER_MY_REQUESTS: "/qa/buyer/my-requests",
    QUOTATION_ACCEPT: (quotationId) => `/qa/quotation/${quotationId}/accept`,
    QUOTATION_REJECT: (quotationId) => `/qa/quotation/${quotationId}/reject`,
    DECISION_CREATE: "/qa/decision",
    DECISION_BY_INSPECTION: (inspectionId) => `/qa/inspection/${inspectionId}/decision`,
    /** QA flows: load inspector company profile by platform user id */
    INSPECTOR_PROFILE_BY_USER: (userId) => `/quality-inspector/profile/by-user/${userId}`,
  },
  // Contract Manufacturer Profile (sellers who are contract manufacturers)
  CONTRACT_MANUFACTURER: {
    MY_PROFILE: "/company/contract-manufacturer/my-profile",
    GET_PROFILE: (companyId) => `/company/profile/${companyId}/contract-manufacturer`,
    SAVE_PROFILE: (companyId) => `/company/profile/${companyId}/contract-manufacturer`,
    UPDATE_STATUS: "/company/contract-manufacturer-status",
    LIST: "/company/contract-manufacturers/list",
    /** Public website — no auth (opted-in manufacturers only) */
    PUBLIC_PROFILE: (companyId) => `/company/contract-manufacturers/${companyId}`,
    /** Public RFQ submit (optional Bearer links buyer account) */
    SUBMIT_RFQ: (companyId) => `/company/contract-manufacturers/${companyId}/rfq`,
    /** Load last saved RFQ for this CM when logged in as buyer */
    MY_RFQ: (companyId) => `/company/contract-manufacturers/${companyId}/rfq/me`,
    /** Broadcast RFQ: count matches before send (optional Bearer) */
    PREVIEW_RFQ_BROADCAST: "/company/contract-manufacturers/rfq/broadcast/preview",
    /** Broadcast RFQ to all manufacturers matching selected industries (optional Bearer) */
    SUBMIT_RFQ_BROADCAST: "/company/contract-manufacturers/rfq/broadcast",
    SUBMIT_RFQ_P2P: "/company/contract-manufacturers/rfq/p2p-submit",
    /** Logged-in buyer: list RFQ rows with status / manufacturer */
    MY_RFQ_SUBMISSIONS: "/company/contract-manufacturers/rfq/my-submissions",
    /** Logged-in buyer: read-only view of a submitted CM RFQ */
    RFQ_SUBMISSION: (id) => `/company/contract-manufacturers/rfq/submission/${id}`,
    /** Logged-in seller (contract manufacturer): RFQs received for their company */
    SELLER_RFQS_RECEIVED: "/company/contract-manufacturer/rfqs-received",
    SELLER_RFQ_DETAIL: (rfqId) => `/company/contract-manufacturer/rfqs-received/${rfqId}`,
    SELLER_RFQ_QUOTE: (rfqId) => `/company/contract-manufacturer/rfqs-received/${rfqId}/quote`,
    /** Seller (CM): view agreement sent by buyer */
    SELLER_AGREEMENT: (rfqId) => `/company/contract-manufacturer/rfqs-received/${rfqId}/agreement`,
    /** Seller (CM): sign agreement (status must be submitted) */
    SELLER_AGREEMENT_SIGN: (rfqId) => `/company/contract-manufacturer/rfqs-received/${rfqId}/agreement/sign`,
    /** Buyer: accept a received quotation (by RFQ id) */
    ACCEPT_QUOTATION: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/quote/accept`,
    /** Buyer: reject a received quotation (by RFQ id) */
    REJECT_QUOTATION: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/quote/reject`,
    /** Buyer: load agreement creation context */
    AGREEMENT_CONTEXT: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/agreement/context`,
    /** Buyer: save agreement draft / submit */
    SAVE_AGREEMENT: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/agreement`,
    /** Buyer: sign agreement after manufacturer has signed */
    BUYER_AGREEMENT_SIGN: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/agreement/sign`,
    /** Buyer: QC requirements */
    QC_REQUIREMENTS_GET: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements`,
    QC_REQUIREMENTS_APPLY: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements/apply`,
    QC_REQUIREMENTS_REJECT_NORMS: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements/reject-norms`,
    QC_REQUIREMENTS_APPROVE_NORMS: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements/approve-norms`,
    QC_INTERNAL_INSPECTION_APPROVE: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements/approve-internal-inspection`,
    QC_INTERNAL_INSPECTION_REJECT: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements/reject-internal-inspection`,
    QC_EXTERNAL_EVALUATOR_DECISION: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/qc-requirements/external-evaluator-decision`,
    QC_REQUIREMENTS_TEMPLATE: "/company/contract-manufacturers/rfq/qc-requirements/template",
    /** Seller (CM): load / update QC requirements buyer applied to this RFQ */
    SELLER_QC_REQUIREMENTS_GET: (rfqId) => `/company/contract-manufacturer/rfqs-received/${rfqId}/qc-requirements`,
    SELLER_QC_REQUIREMENTS_APPLY: (rfqId) =>
      `/company/contract-manufacturer/rfqs-received/${rfqId}/qc-requirements/apply`,
    SELLER_QC_REQUIREMENTS_TEMPLATE: "/company/contract-manufacturer/rfqs-received/qc-requirements/template",
    /** CM seller: configurable post-agreement production / logistics pipeline */
    SELLER_PRODUCTION_PROCESS_GET: (rfqId) =>
      `/company/contract-manufacturer/rfqs-received/${rfqId}/production-process`,
    SELLER_PRODUCTION_PROCESS_START: (rfqId) =>
      `/company/contract-manufacturer/rfqs-received/${rfqId}/production-process/start`,
    SELLER_PRODUCTION_PROCESS_COMPLETE_STAGE: (rfqId) =>
      `/company/contract-manufacturer/rfqs-received/${rfqId}/production-process/complete-stage`,
    /** CM logistics: synthetic PO + snapshot of invoice / e-way / manifest / GRN */
    SELLER_FULFILLMENT_ENSURE_PO: (rfqId) =>
      `/company/contract-manufacturer/rfqs-received/${rfqId}/fulfillment/ensure-po`,
    SELLER_FULFILLMENT: (rfqId) => `/company/contract-manufacturer/rfqs-received/${rfqId}/fulfillment`,
    /** CM seller: same payload shape as buyer delivery confirmation (tracking UI), after buyer accepts delivery */
    SELLER_DELIVERY_TRACKING_GET: (rfqId) =>
      `/company/contract-manufacturer/rfqs-received/${rfqId}/delivery-tracking`,
    BUYER_FULFILLMENT: (rfqId) => `/company/contract-manufacturers/rfq/${rfqId}/fulfillment`,
    BUYER_PRODUCTION_PROCESS_GET: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/production-process`,
    BUYER_PRODUCTION_PROCESS_COMPLETE_STAGE: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/production-process/complete-stage`,
    BUYER_DELIVERY_CONFIRMATION_GET: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/delivery-confirmation`,
    BUYER_DELIVERY_CONFIRMATION_ACCEPT: (rfqId) =>
      `/company/contract-manufacturers/rfq/${rfqId}/delivery-confirmation/accept`,
  },
  /** Module ↔ logistics_generic_rfq bridge (Buyer/Seller, Auction, CM) */
  MODULE_LOGISTICS: {
    SUMMARY: "/module-logistics/summary",
    INITIATE: "/module-logistics/initiate",
    LINK: "/module-logistics/link",
  },
  /** Logistics provider directory (public browse) + public profile by company id */
  LOGISTICS_PROVIDER: {
    LIST: "/company/logistics-providers/list",
    PUBLIC_PROFILE: (companyId) => `/company/logistics-providers/${companyId}`,
    /** `logistics_generic_rfq` — browse: no query; dashboard list: `?detail=1` (ref, kind, submitted_at, contact from payload). */
    RFQ_MY_SUBMISSIONS: "/company/logistics-providers/rfq/my-submissions",
    RFQ_MY_SUBMISSION_QUOTES: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/quotes`,
    RFQ_MY_SUBMISSION_QUOTE_DETAILS: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/quotes/${quoteId}`,
    RFQ_MY_SUBMISSION_ACCEPT_QUOTE: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/quotes/${quoteId}/accept`,
    RFQ_MY_SUBMISSION_REJECT_QUOTE: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/quotes/${quoteId}/reject`,
    RFQ_MY_SUBMISSION_HANDOVER_GET: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/quotes/${quoteId}/handover`,
    RFQ_MY_SUBMISSION_HANDOVER_SAVE: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/quotes/${quoteId}/handover`,
    RFQ_MY_SUBMISSION_DELIVERY_PROCESS: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/delivery-process`,
    RFQ_MY_SUBMISSION_DELIVERY_PROCESS_ADVANCE: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/delivery-process/advance`,
    /** Completes current `current_stage_key` stage (strict); preferred name vs `/advance`. */
    RFQ_MY_SUBMISSION_DELIVERY_PROCESS_COMPLETE_CURRENT: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/delivery-process/complete-current`,
    /** Buyer (RFQ owner): GET invoice; POST accept (idempotent). */
    RFQ_MY_SUBMISSION_INVOICE: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/invoice`,
    RFQ_MY_SUBMISSION_INVOICE_ACCEPT: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/invoice/accept`,
    /** Buyer (RFQ owner): POST mark invoice paid (idempotent) after payment gateway success. */
    RFQ_MY_SUBMISSION_INVOICE_MARK_PAID: (submissionId) =>
      `/company/logistics-providers/rfq/my-submissions/${submissionId}/invoice/mark-paid`,
    /** Logged-in logistics provider: RFQs targeting this company (`logistics_provider_company_id`). */
    RFQ_RECEIVED_FOR_PROVIDER: "/company/logistics-providers/rfq/received",
    RFQ_MATCH: "/company/logistics-providers/rfq/match",
    RFQ_GENERIC: "/company/logistics-providers/rfq/generic",
    RFQ_GENERIC_BATCH: "/company/logistics-providers/rfq/generic/batch",
    LOGISTICS_NOTIFICATIONS: "/company/logistics-providers/notifications",
    LOGISTICS_NOTIFICATIONS_UNREAD: "/company/logistics-providers/notifications/unread-count",
    RFQ_RECEIVED_ACCEPT_GRN: (submissionId) =>
      `/company/logistics-providers/rfq/received/${submissionId}/accept-grn`,
    RFQ_SUBMISSION_QUOTE: (submissionId) =>
      `/company/logistics-providers/rfq/submission/${submissionId}/quote`,
    RFQ_SUBMISSION_BUYER_COMPANY: (submissionId) =>
      `/company/logistics-providers/rfq/submission/${submissionId}/buyer-company`,
    /** GET: fetch persisted invoice; POST: create (idempotent per submission + quote). */
    RFQ_SUBMISSION_INVOICE: (submissionId) =>
      `/company/logistics-providers/rfq/submission/${submissionId}/invoice`,
    LOGISTICS_RFQ_QUOTE_GET: "/company/logistics-providers/rfq/submission",
    RFQ_PICKUP_CONTEXT: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/submission/${submissionId}/pickup-context/${quoteId}`,
    RFQ_PICKUP_CONFIRM: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/submission/${submissionId}/pickup-confirm/${quoteId}`,
    RFQ_SUBMISSION_COMPLETE_DELIVERY_STAGE: (submissionId, quoteId) =>
      `/company/logistics-providers/rfq/submission/${submissionId}/delivery-stage/${quoteId}/complete`,
  },
  UPLOAD: {
    MULTIPLE: "/upload/multiple",
  },
  NOTIFICATIONS: {
    BASE: "/notifications",
    RECENT: "/notifications/recent",
    UNREAD_COUNT: "/notifications/unread-count",
    MARK_READ: "/notifications/mark-read",
    MARK_ALL_READ: "/notifications/mark-all-read",
    DETAIL: (id) => `/notifications/${id}`,
    OPEN: (id) => `/notifications/${id}/open`,
  },
  CONTACT: {
    SUBMIT: "/public/platform/contact",
  },
  DISPUTES: {
    SUBMIT: "/public/platform/disputes",
    MY_LIST: "/me/disputes",
    MY_STATS: "/me/disputes/stats",
    MY_DETAIL: (id) => `/me/disputes/${id}`,
  },
};
